import { Stack, Text } from "@mantine/core";
import {
  IconBuilding,
  IconChartBar,
  IconFileDescription,
  IconIdBadge2,
  IconTool,
} from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  filterTutorialSections,
  TutorialPageShell,
  TutorialSection,
  TutorialSectionCard,
} from "@components/tutorial/TutorialShared";

export default function DistributorTutorialPage() {
  const { t } = useTranslation("tutorial");
  const [query, setQuery] = useState("");
  const sections: TutorialSection[] = [
    {
      title: t("distributorTutorialPage.sections.distributorDashboard.title"),
      description: t("distributorTutorialPage.sections.distributorDashboard.description"),
      icon: <IconBuilding size={18} />,
      actions: [
        {
          title: t(
            "distributorTutorialPage.sections.distributorDashboard.actions.openDistributorWorkspace.title",
          ),
          location: t(
            "distributorTutorialPage.sections.distributorDashboard.actions.openDistributorWorkspace.location",
          ),
          steps: t(
            "distributorTutorialPage.sections.distributorDashboard.actions.openDistributorWorkspace.steps",
            { returnObjects: true },
          ) as string[],
        },
        {
          title: t(
            "distributorTutorialPage.sections.distributorDashboard.actions.createAccountRequest.title",
          ),
          location: t(
            "distributorTutorialPage.sections.distributorDashboard.actions.createAccountRequest.location",
          ),
          steps: t(
            "distributorTutorialPage.sections.distributorDashboard.actions.createAccountRequest.steps",
            { returnObjects: true },
          ) as string[],
        },
        {
          title: t(
            "distributorTutorialPage.sections.distributorDashboard.actions.createOfflineLicenseRequest.title",
          ),
          location: t(
            "distributorTutorialPage.sections.distributorDashboard.actions.createOfflineLicenseRequest.location",
          ),
          steps: t(
            "distributorTutorialPage.sections.distributorDashboard.actions.createOfflineLicenseRequest.steps",
            { returnObjects: true },
          ) as string[],
        },
      ],
    },
    {
      title: t("distributorTutorialPage.sections.organizationManagement.title"),
      description: t("distributorTutorialPage.sections.organizationManagement.description"),
      icon: <IconIdBadge2 size={18} />,
      actions: [
        {
          title: t(
            "distributorTutorialPage.sections.organizationManagement.actions.openOrgRecord.title",
          ),
          location: t(
            "distributorTutorialPage.sections.organizationManagement.actions.openOrgRecord.location",
          ),
          steps: t(
            "distributorTutorialPage.sections.organizationManagement.actions.openOrgRecord.steps",
            { returnObjects: true },
          ) as string[],
        },
        {
          title: t(
            "distributorTutorialPage.sections.organizationManagement.actions.reviewPending.title",
          ),
          location: t(
            "distributorTutorialPage.sections.organizationManagement.actions.reviewPending.location",
          ),
          steps: t(
            "distributorTutorialPage.sections.organizationManagement.actions.reviewPending.steps",
            { returnObjects: true },
          ) as string[],
        },
      ],
    },
    {
      title: t("distributorTutorialPage.sections.analyticsInstaller.title"),
      description: t("distributorTutorialPage.sections.analyticsInstaller.description"),
      icon: <IconChartBar size={18} />,
      actions: [
        {
          title: t(
            "distributorTutorialPage.sections.analyticsInstaller.actions.openAnalytics.title",
          ),
          location: t(
            "distributorTutorialPage.sections.analyticsInstaller.actions.openAnalytics.location",
          ),
          steps: t(
            "distributorTutorialPage.sections.analyticsInstaller.actions.openAnalytics.steps",
            { returnObjects: true },
          ) as string[],
        },
        {
          title: t(
            "distributorTutorialPage.sections.analyticsInstaller.actions.useInstaller.title",
          ),
          location: t(
            "distributorTutorialPage.sections.analyticsInstaller.actions.useInstaller.location",
          ),
          steps: t(
            "distributorTutorialPage.sections.analyticsInstaller.actions.useInstaller.steps",
            { returnObjects: true },
          ) as string[],
          note: t("distributorTutorialPage.sections.analyticsInstaller.actions.useInstaller.note"),
        },
      ],
    },
    {
      title: t("distributorTutorialPage.sections.documentation.title"),
      description: t("distributorTutorialPage.sections.documentation.description"),
      icon: <IconFileDescription size={18} />,
      actions: [
        {
          title: t(
            "distributorTutorialPage.sections.documentation.actions.openDistributorDocs.title",
          ),
          location: t(
            "distributorTutorialPage.sections.documentation.actions.openDistributorDocs.location",
          ),
          steps: t(
            "distributorTutorialPage.sections.documentation.actions.openDistributorDocs.steps",
            { returnObjects: true },
          ) as string[],
        },
      ],
    },
    {
      title: t("distributorTutorialPage.sections.notCoveredHere.title"),
      description: t("distributorTutorialPage.sections.notCoveredHere.description"),
      icon: <IconTool size={18} />,
      actions: [
        {
          title: t("distributorTutorialPage.sections.notCoveredHere.actions.adminOnlyTools.title"),
          location: t(
            "distributorTutorialPage.sections.notCoveredHere.actions.adminOnlyTools.location",
          ),
          steps: t("distributorTutorialPage.sections.notCoveredHere.actions.adminOnlyTools.steps", {
            returnObjects: true,
          }) as string[],
        },
      ],
    },
  ];

  const filteredSections = useMemo(
    () => filterTutorialSections(sections, query),
    [sections, query],
  );
  const actionCount = filteredSections.reduce(
    (count, section) => count + section.actions.length,
    0,
  );

  return (
    <TutorialPageShell
      badge={t("distributorTutorialPage.badge")}
      title={t("distributorTutorialPage.title")}
      description={t("distributorTutorialPage.description")}
      searchValue={query}
      onSearchChange={setQuery}
      resultCount={actionCount}
      relatedLinks={[
        {
          label: t("distributorTutorialPage.relatedLinks.backToShared.label"),
          to: "/tutorial",
          description: t("distributorTutorialPage.relatedLinks.backToShared.description"),
        },
      ]}
    >
      <Stack gap="xl">
        {filteredSections.length === 0 ? (
          <Text c="dimmed">{t("distributorTutorialPage.noResults")}</Text>
        ) : null}
        {filteredSections.map((section) => (
          <TutorialSectionCard key={section.title} section={section} />
        ))}
      </Stack>
    </TutorialPageShell>
  );
}
